import { useContext, useEffect, useRef } from 'react'
import { UNSAFE_NavigationContext } from 'react-router'
import { validateMediaUploadFile } from './mediaAttachments'

const UNSIGNED_INTEGER_PATTERN = /^\d+$/

export const AI_ANALYSIS_DISCLAIMER = 'AI-generated — may misidentify names or details. Verify against the video.'

export function parseStoredJson(key, fallback = null) {
  if (!key || typeof window === 'undefined') return fallback

  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

export function persistStoredJson(key, value) {
  if (!key || typeof window === 'undefined') return
  window.localStorage.setItem(key, JSON.stringify(value))
}

export function removeStoredValue(key) {
  if (!key || typeof window === 'undefined') return
  window.localStorage.removeItem(key)
}

export function parseAiJson(value) {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export function isNumericExternalId(value) {
  return UNSIGNED_INTEGER_PATTERN.test(String(value || ''))
}

export function parseNumericOutcomeId(value) {
  if (!isNumericExternalId(value)) return null
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

export function blockImplicitFormSubmit(event) {
  if (event.key !== 'Enter' || event.defaultPrevented) return

  const tagName = event.target?.tagName?.toUpperCase()
  if (tagName === 'INPUT' || tagName === 'SELECT') {
    event.preventDefault()
  }
}

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:')
}

export function useBlobPreviewUrlCleanup(uploadedMedia) {
  const latestUploadedMediaRef = useRef(uploadedMedia)
  latestUploadedMediaRef.current = uploadedMedia

  useEffect(() => () => {
    latestUploadedMediaRef.current
      .map((media) => media.previewUrl)
      .filter(isBlobUrl)
      .forEach((url) => URL.revokeObjectURL(url))
  }, [])
}

export function usePendingMediaReplacement(onReplace, inputRef) {
  const pendingReplaceId = useRef(null)
  const focusCleanupRef = useRef(null)

  const clearPendingReplacement = () => {
    pendingReplaceId.current = null
    focusCleanupRef.current?.()
    focusCleanupRef.current = null
  }

  const beginReplacement = (id) => {
    clearPendingReplacement()
    pendingReplaceId.current = id

    const inputElement = inputRef?.current
    let focusTimer = null
    const handleCancel = () => {
      if (pendingReplaceId.current === id) clearPendingReplacement()
    }
    const handleFocus = () => {
      window.removeEventListener('focus', handleFocus)
      focusTimer = window.setTimeout(() => {
        if (pendingReplaceId.current === id) clearPendingReplacement()
      }, 300)
    }

    inputElement?.addEventListener('cancel', handleCancel)
    if (typeof window !== 'undefined') window.addEventListener('focus', handleFocus)
    focusCleanupRef.current = () => {
      inputElement?.removeEventListener('cancel', handleCancel)
      if (typeof window !== 'undefined') window.removeEventListener('focus', handleFocus)
      if (focusTimer !== null) window.clearTimeout(focusTimer)
    }
  }

  const consumeReplacement = (files) => {
    const id = pendingReplaceId.current
    const accepted = files.length > 0 && files.every((file) => !validateMediaUploadFile(file))
    clearPendingReplacement()
    if (!id || !accepted) return false
    onReplace?.(id)
    return true
  }

  useEffect(() => () => clearPendingReplacement(), [])

  return { beginReplacement, consumeReplacement }
}

export function useUnsavedChangesGuard(when, message) {
  const navigationContext = useContext(UNSAFE_NavigationContext)
  const navigator = navigationContext?.navigator

  useEffect(() => {
    if (!when || typeof navigator?.block !== 'function') {
      return undefined
    }

    const unblock = navigator.block((tx) => {
      const shouldLeave = window.confirm(message)
      if (!shouldLeave) {
        return
      }

      unblock()
      tx.retry()
    })

    return unblock
  }, [message, navigator, when])
}
